import { Component, Inject, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import moment from 'moment';
import { CheckInErrorData, WorkflowStepData } from 'app/core/interfaces/check-in-error.interface';
import { VisitsV2Service } from 'app/modules/davesa/apps/visits/visits-v2.service';
import { ParticipantsV2Service } from 'app/modules/davesa/apps/participants/participants-v2.service';
import { ErrorHandlerUtil } from 'app/core/util/error-handler.util';
import { DateTransformPipe } from 'app/core/pipes/dateTransform.pipe';

@Component({
  selector: 'check-in-error-dialog',
  templateUrl: './check-in-error-dialog.component.html',
  styleUrls: ['./check-in-error-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    DatePipe,
    DateTransformPipe
  ]
})
export class CheckInErrorDialogComponent {
  private _cdr = inject(ChangeDetectorRef);
  private _visitsV2Service = inject(VisitsV2Service);
  private _participantsV2Service = inject(ParticipantsV2Service);

  workflowSteps: WorkflowStepData[] = [];
  isUpdating = false;
  hasError = false;
  noSignatureStepsError: string | null = null; // Error message when no signature steps configured
  private originalErrorReasons: Map<number, string> = new Map(); // Store original error reasons by step index
  private originalBlockedUserIds: Map<number, number | null> = new Map(); // Store original blocked user_id by step index

  constructor(
    public dialogRef: MatDialogRef<CheckInErrorDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CheckInErrorData
  ) {
    this.workflowSteps = data.workflowSteps || [];
    
    // Check for no signature steps error from initial error details
    if (data.errorDetails) {
      const noSignatureStepsMessage = this.extractNoSignatureStepsError(data.errorDetails);
      if (noSignatureStepsMessage) {
        this.noSignatureStepsError = noSignatureStepsMessage;
      } else if (this.workflowSteps.length > 0) {
        // Map errors from errorDetails to workflow steps if not already mapped
        // This ensures deleted/inactive personnel errors are displayed even if user is not in personnel list
        this.mapErrorToWorkflowSteps(data.errorDetails);
      }
    }
    
    // Store original error reasons and blocked user_ids for each step
    this.workflowSteps.forEach((step, index) => {
      if (step.errorReason && step.errorReason.trim()) {
        this.originalErrorReasons.set(index, step.errorReason);
        // Store the user_id that was causing the error (the initially selected user)
        this.originalBlockedUserIds.set(index, step.selectedUserId);
      }
    });
    
    // Initialize hasError to false
    // hasError is only set to true when API update operation fails (in onUpdateAndRetry catch block)
    // Initial state errors (from dialog opening) are handled by hasAnyError() checking errorReason and unselected steps
    // hasError is cleared to false when user fixes all validation errors (in onPersonnelChange)
    this.hasError = false;
  }

  /**
   * Check if there are any errors (validation errors or API errors)
   * Disable button when:
   * - No signature steps configured (noSignatureStepsError)
   * - Any step has no user selected (selectedUserId === null)
   * - Any step has errorReason (user is blocked/has error)
   * - Has API error from update operation
   */
  hasAnyError(): boolean {
    // Check for no signature steps error
    if (this.noSignatureStepsError) {
      return true;
    }

    // Check for API errors from update operation
    if (this.hasError) {
      return true;
    }

    // Check if any step is missing selection (no user selected)
    const hasUnselectedStep = this.workflowSteps.some(step => !step.selectedUserId);
    if (hasUnselectedStep) {
      return true;
    }

    // Check if any workflow step has error reason (user is blocked/has error)
    const hasStepErrorReason = this.workflowSteps.some(step => step.errorReason && step.errorReason.trim());
    if (hasStepErrorReason) {
      return true;
    }
    
    return false;
  }

  /**
   * Handle personnel selection change
   */
  onPersonnelChange(stepIndex: number, userId: number | string | null): void {
    if (stepIndex < 0 || stepIndex >= this.workflowSteps.length) return;
    
    const step = this.workflowSteps[stepIndex];
    const selectedUserId = userId ? Number(userId) : null;
    step.selectedUserId = selectedUserId;
    step.isTouched = true;
    
    // Get the original blocked user_id for this step
    const originalBlockedUserId = this.originalBlockedUserIds.get(stepIndex);
    
    // Check if the newly selected user is the same as the original blocked user
    if (selectedUserId && originalBlockedUserId !== null && originalBlockedUserId !== undefined) {
      if (selectedUserId === originalBlockedUserId) {
        // If user selected is the same as the original blocked user, restore error reason
        const originalErrorReason = this.originalErrorReasons.get(stepIndex);
        if (originalErrorReason) {
          step.errorReason = originalErrorReason;
        } else {
          // If no original error reason stored, try to get it from error details
          this.restoreErrorReasonForStep(step, stepIndex);
        }
      } else {
        // If user selected is different from the original blocked user, clear error reason
        step.errorReason = '';
      }
    } else if (!selectedUserId) {
      // If no user selected, clear error reason
      step.errorReason = '';
    } else if (originalBlockedUserId === null || originalBlockedUserId === undefined) {
      // If this step didn't have a blocked user originally, clear any error reason
      step.errorReason = '';
    }
    
    // Check if there are any validation errors (unselected steps or error reasons)
    const hasUnselectedStep = this.workflowSteps.some(step => !step.selectedUserId);
    const hasStepErrorReason = this.workflowSteps.some(step => step.errorReason && step.errorReason.trim());
    
    // Clear hasError flag if no validation errors remain
    // hasError should only be true for API errors from update operation
    // When user changes selection and fixes all validation errors, clear hasError
    if (!hasUnselectedStep && !hasStepErrorReason) {
      this.hasError = false;
    }
    
    // Force change detection to update button state immediately
    this._cdr.detectChanges();
  }


  /**
   * Mark step as touched when dropdown is focused
   */
  onPersonnelDropdownFocus(stepIndex: number): void {
    if (stepIndex >= 0 && stepIndex < this.workflowSteps.length) {
      this.workflowSteps[stepIndex].isTouched = true;
      this._cdr.detectChanges();
    }
  }

  /**
   * Validate all selections
   */
  validateSelections(): boolean {
    let isValid = true;
    
    // Check each step and mark as touched if validation fails
    this.workflowSteps.forEach(step => {
      if (!step.selectedUserId) {
        step.isTouched = true;
        isValid = false;
      }
    });
    
    this._cdr.detectChanges();
    return isValid;
  }

  /**
   * Check if a step has error (not selected and touched)
   */
  hasStepError(step: any): boolean {
    return step.isTouched && !step.selectedUserId;
  }

  /**
   * Get error message for a step
   */
  getStepErrorMessage(step: any): string {
    if (this.hasStepError(step)) {
      return `Please select a staff member for "${step.stepLabel}".`;
    }
    return '';
  }

  /**
   * Update personnel and retry check-in
   */
  async onUpdateAndRetry(): Promise<void> {
    if (!this.validateSelections() || !this.data.visitOid) {
      return;
    }

    this.isUpdating = true;
    this.hasError = false;
    this.noSignatureStepsError = null;

    try {
      // Get current visit data to include required fields in update
      const currentVisit = await this._visitsV2Service.getVisitByOid(this.data.visitOid);
      
      // Prepare personnel updates
      const personnelUpdates = this.workflowSteps.map(step => {
        const selectedPersonnel = step.personnel.find(p => p.userId === step.selectedUserId);
        const roleId = selectedPersonnel?.delegationRoleId || step.roleId;
        
        return {
          role_id: roleId,
          user_id: step.selectedUserId!
        };
      });

      // Prepare update data with all required fields (like reschedule)
      const startDate = currentVisit?.appointment_start_date || currentVisit?.start_date;
      const endDate = currentVisit?.appointment_end_date || currentVisit?.end_date;
      
      const personnelUpdateData: any = {
        oid: this.data.visitOid,
        signature_assignments: personnelUpdates,
        appointment_start_date: startDate,
        appointment_end_date: endDate,
        appointment_start_time: startDate ? moment(startDate).format('h:mm A') : undefined,
        is_all_date_required: currentVisit?.is_all_date_required || false,
        message_type: currentVisit?.message_type || 'email',
        location_type: currentVisit?.location_type,
        guardians: (currentVisit?.guardians || []).map((guardian: any) => {
          const mappedGuardian = { ...guardian };
          if (guardian?.country_code) {
            mappedGuardian.country_code = guardian.country_code;
          }
          return mappedGuardian;
        })
      };

      // Step 1: Update personnel assignments only (no status change)
      await this._visitsV2Service.updateVisitItem(personnelUpdateData);

      // Get the latest visit data after personnel update to get numeric ID
      const latestVisit = await this._visitsV2Service.getVisitByOid(this.data.visitOid);

      // Step 2: If target status is "clinic", call check-in API (like clinic-now-list)
      if (this.data.targetStatus === 'clinic') {
        const subjectVisitId = latestVisit?.id || latestVisit?.visit_id;
        
        if (!subjectVisitId || isNaN(Number(subjectVisitId))) {
          throw new Error('Invalid visit ID for check-in. Visit ID is required and must be a valid number.');
        }

        // Call check-in API with subject_visit_id (same as clinic-now-list)
        await this._participantsV2Service.checkIn({
          subject_visit_id: Number(subjectVisitId)
        });
      } else if (this.data.targetStatus) {
        // For other statuses, use updateVisitItem
        const statusUpdateData: any = {
          oid: latestVisit?.oid || this.data.visitOid,
          status: this.data.targetStatus
        };

        await this._visitsV2Service.updateVisitItem(statusUpdateData);
      }

      // Close dialog on success
      this.dialogRef.close({ success: true, retry: true });
    } catch (error) {
      console.error('Error updating personnel:', error);
      this.hasError = true;

      // Check for no signature steps error
      const noSignatureStepsMessage = this.extractNoSignatureStepsError(error);
      if (noSignatureStepsMessage) {
        this.noSignatureStepsError = noSignatureStepsMessage;
      } else {
        // Parse error response and map to workflow steps
        this.mapErrorToWorkflowSteps(error);
      }
    } finally {
      this.isUpdating = false;
      this._cdr.detectChanges();
    }
  }

  /**
   * Map error response to workflow steps
   */
  private mapErrorToWorkflowSteps(error: any): void {
    try {
      const allErrors = this.extractErrorsFromResponse(error);
      const errorData = ErrorHandlerUtil.extractErrorData(error);
      const reasons = errorData?.reasons || {};
      
      this.workflowSteps = this.workflowSteps.map((step, index) => {
        const matchingError = this.findMatchingError(step, allErrors);
        let errorReason = matchingError?.reason || '';
        let isTouched = step.isTouched || false;
        
        // If step has no user selected and error is missing_roles, don't set errorReason
        // Set isTouched to true to show "Please select" message instead
        if (!step.selectedUserId && matchingError) {
          const isMissingRolesError = reasons.missing_roles?.some((err: any) => 
            err.signature_step_id === matchingError.signature_step_id ||
            err.step_label === matchingError.step_label
          );
          if (isMissingRolesError) {  
            errorReason = ''; // Clear errorReason to show "Please select" instead
            isTouched = true; // Set isTouched to trigger hasStepError
          }
        }
        
        // If error found and has user_id, store it as original blocked user_id
        // This is important for deleted/inactive personnel who may not be in personnel list
        if (matchingError && matchingError.user_id) {
          const errorUserId = Number(matchingError.user_id);
          // Only set if not already set or if it's different from current selectedUserId
          if (!this.originalBlockedUserIds.has(index) || 
              this.originalBlockedUserIds.get(index) !== errorUserId) {
            this.originalBlockedUserIds.set(index, errorUserId);
          }
        }
        
        return {
          ...step,
          errorReason: errorReason,
          isTouched: isTouched
        };
      });

      this._cdr.detectChanges();
    } catch (parseError) {
      console.error('Error parsing error response:', parseError);
    }
  }

  /**
   * Extract no signature steps error message from API response
   */
  private extractNoSignatureStepsError(error: any): string | null {
    const errorData = ErrorHandlerUtil.extractErrorData(error);
    const summary = errorData?.summary;
    
    // Check if no_signature_steps is true
    if (summary?.no_signature_steps === true) {
      // Use ErrorHandlerUtil to extract error message
      const message = ErrorHandlerUtil.extractErrorMessage(error, 'general');
      if (message) {
        return message;
      }
      
      // Fallback to reasons array
      const reasons = errorData?.reasons;
      if (Array.isArray(reasons) && reasons.length > 0) {
        return reasons[0];
      }
      
      // Default message
      return 'At least one signature step in Signature Workflow must be provided!';
    }
    
    // Check if reasons array contains "No signature steps configured"
    const reasons = errorData?.reasons;
    if (Array.isArray(reasons)) {
      const noSignatureStepReason = reasons.find((reason: string) => 
        typeof reason === 'string' && 
        (reason.toLowerCase().includes('no signature steps') || 
         reason.toLowerCase().includes('signature workflow'))
      );
      if (noSignatureStepReason) {
        // Use ErrorHandlerUtil to extract error message
        const message = ErrorHandlerUtil.extractErrorMessage(error, 'general');
        return message || noSignatureStepReason;
      }
    }
    
    return null;
  }

  /**
   * Extract all errors from API response
   */
  private extractErrorsFromResponse(error: any): any[] {
    const errorData = ErrorHandlerUtil.extractErrorData(error);
    const reasons = errorData?.reasons;
    
    if (!reasons) return [];

    return [
      ...(reasons.expired_personnel || []),
      ...(reasons.missing_roles || []),
      ...(reasons.removing_personnel || []),
      ...(reasons.removed_personnel || []),
      ...(reasons.inactive_personnel || []),
      ...(reasons.deleted_personnel || [])
    ];
  }

  /**
   * Find matching error for a workflow step
   */
  private findMatchingError(step: WorkflowStepData, allErrors: any[]): any {
    // Try matching by signature step ID
    let matchingError = allErrors.find(err => 
      err.signature_step_id === step.signatureStepId
    );
    
    // Fallback to matching by step label
    if (!matchingError) {
      matchingError = allErrors.find(err => 
        err.step_label === step.stepLabel
      );
    }

    return matchingError || null;
  }

  /**
   * Find matching error reason for a workflow step
   */
  private findMatchingErrorReason(step: WorkflowStepData, allErrors: any[]): string {
    const matchingError = this.findMatchingError(step, allErrors);
    return matchingError?.reason || '';
  }

  /**
   * Restore error reason for a step from error details
   */
  private restoreErrorReasonForStep(step: WorkflowStepData, stepIndex: number): void {
    if (!this.data?.errorDetails) return;
    
    try {
      const allErrors = this.extractErrorsFromResponse(this.data.errorDetails);
      const errorReason = this.findMatchingErrorReason(step, allErrors);
      
      if (errorReason) {
        step.errorReason = errorReason;
        // Store it as original error reason if not already stored
        if (!this.originalErrorReasons.has(stepIndex)) {
          this.originalErrorReasons.set(stepIndex, errorReason);
        }
      }
    } catch (error) {
      console.error('Error restoring error reason:', error);
    }
  }

  /**
   * Close dialog
   */
  public onClose(): void {
    this.dialogRef.close();
  }

  /**
   * Get full name from personnel object
   */
  getFullName(person: any): string {
    if (!person) return '';
    const parts = [
      person.firstName,
      person.middleName,
      person.lastName
    ].filter(p => p && p.trim());

    let fullName = parts.join(' ');
    if (person.suffixName) {
      fullName += `, ${person.suffixName}`;
    }
    return fullName;
  }

}

